import { FC } from 'react';
import { useSetAtom } from 'jotai';
import type { ChangeMatchPayload } from './types-well';
import { changeMatchAtom } from './atoms/wells';
import { WellPickerPanel } from '../../afe-catalog/structuring/well-picker-panel';

// DO Change Match. Thin DO-specific wrapper over the SHARED `WellPickerPanel`
// (AFE-owned, prop-parameterized): supplies the copy + the DO `changeMatchAtom`
// mutation, seeds the search with the staged well name, and wraps the resolved
// selection under the `match` payload key. POSTs to
// `resources.matchCandidate.href` (endpoint-agnostic — the DO show/overview both
// emit this href). Passes `selectorPageContent` so the slide-in anchors to the
// DO shell root.

interface Props {
  open: boolean;
  matchCandidateHref?: string;
  stagedWellName: string;
  onClose: () => void;
  onChanged: () => void;
}

export const ChangeMatchPanel: FC<Props> = ({
  open,
  matchCandidateHref,
  stagedWellName,
  onClose,
  onChanged,
}) => {
  const changeMatch = useSetAtom(changeMatchAtom);

  return (
    <WellPickerPanel
      open={open}
      onClose={onClose}
      title="Change Match"
      subtitle="Search for a different well or create an imported one."
      idPrefix="do-cm"
      selectorPageContent=".do-structuring-shell"
      primaryButtonText="Change Match"
      initialSearchValue={stagedWellName}
      onSubmit={(selection, { setPending, setError }) => {
        if (!matchCandidateHref) return;
        // Narrow per branch so the discriminated `ChangeMatchPayload`
        // distributes (TS won't assign `{ match: A | B }` to the union).
        const payload: ChangeMatchPayload =
          selection.kind === 'existing_well'
            ? { match: selection }
            : { match: selection };
        setPending(true);
        setError(null);
        changeMatch({
          url: matchCandidateHref,
          method: 'POST',
          data: payload,
          onSuccess: () => {
            setPending(false);
            onChanged();
          },
          onError: (m) => {
            setPending(false);
            setError(m ?? 'Failed to change match.');
          },
        });
      }}
    />
  );
};
